import type { ReactElement } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { useSlider } from './SliderContext';

interface SlideProps {
  className: string;
  item: ReactElement;
  id: number;
  animation: string;
  width: number;
  active: boolean;
  currentIndex: number;
  slideNumber: number;
  speed: number;
  isDragging: boolean;
  manualOpacity?: number;
}

export function Slide({
  className,
  item,
  id,
  animation,
  width,
  active,
  currentIndex,
  slideNumber,
  speed,
  isDragging,
  manualOpacity
}: SlideProps) {
  const slide = useRef(null);
  const [show, toggleShow] = useState(true);
  const { appClassnamePrefix } = useSlider();

  useLayoutEffect(() => {
    toggleShow(currentIndex === id);
  }, [currentIndex, id]);

  const getOpacity = () => {
    if (isDragging && manualOpacity !== undefined) {
      return manualOpacity;
    }

    if (animation === 'slide' || show) {
      return 1;
    }

    return 0;
  };

  return (
    <StyledSlide
      role="group"
      aria-roledescription="slide"
      aria-label={`Slide ${slideNumber}`}
      aria-hidden={!active}
      inert={active ? undefined : ''}
      ref={slide}
      _active={active}
      _animation={animation}
      _appClassPrefix={appClassnamePrefix}
      _derivedClassname={className}
      _id={id}
      _opacity={getOpacity()}
      _speed={isDragging ? 0 : speed}
      _width={width}
    >
      {item}
    </StyledSlide>
  );
}

interface StyledSlideProps {
  _appClassPrefix: string;
  _derivedClassname: string;
  _animation: string;
  _width: number;
  _id: number;
  _active: boolean;
  _opacity: number;
  _speed: number;
  inert?: string;
}

const StyledSlide = styled.div.attrs<StyledSlideProps>(
  ({ _appClassPrefix, _derivedClassname }) => ({
    className: classNames(`${_appClassPrefix}-slide-slide`, _derivedClassname)
  })
)`
  display: flex;
  overflow: hidden;
  flex-grow: 1;
  flex-shrink: 0;
  box-sizing: border-box;

  ${({ _animation, _width, _id, _active, _opacity, _speed }) => css`
    ${_animation === 'fade' &&
    `transform: translate3d(${-_width * _id}%, 0, 0);`}
    ${!_active && 'pointer-events: none;'}
    opacity: ${_opacity};
    z-index: ${_active ? 1 : 2};
    transition: ${_speed}s ease opacity;
  `}
`;
